import React, { useId } from "react";
import classNames from "classnames";
import type { ToggleProps } from "./types";
import styles from "./Toggle.module.css";

/**
 * Toggle component - An animated switch with on/off labels.
 * Ported from Vue Toggle.vue component.
 *
 * WCAG AAA Compliant:
 * - Accessible label via aria-label
 * - Keyboard navigation (Enter/Space to toggle)
 * - Text labels are clickable with keyboard support
 * - Proper role="switch" with aria-checked state
 * - Minimum touch target size enforced via CSS
 */
export const Toggle = React.forwardRef<HTMLDivElement, ToggleProps>(
  (
    {
      checked = false,
      textOn = "On",
      textOff = "Off",
      compact = false,
      responsiveCompact = false,
      compactLabel,
      disabled = false,
      size = "md",
      label,
      onChange,
      "data-testid": testId,
    },
    ref,
  ) => {
    const generatedId = useId();
    const toggleId = testId || `toggle-${generatedId}`;

    const handleToggle = () => {
      if (!disabled && onChange) {
        onChange(!checked);
      }
    };

    const handleSetOff = () => {
      if (!disabled && onChange && checked) {
        onChange(false);
      }
    };

    const handleSetOn = () => {
      if (!disabled && onChange && !checked) {
        onChange(true);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleToggle();
      }
    };

    // Handle keyboard activation for text labels (WCAG 2.1.1)
    const handleTextKeyDown = (e: React.KeyboardEvent, action: () => void) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        action();
      }
    };

    // Generate accessible label for the switch
    const accessibleLabel = label || `Toggle between ${textOff} and ${textOn}`;
    const currentStateLabel = checked ? textOn : textOff;

    // Resolve the label for compact mode
    const resolvedCompactLabel = compactLabel || textOn;

    // Compact mode: left label always has strikethrough, right label is always normal
    // Left = "off/disabled" state, Right = "on/enabled" state
    if (compact) {
      return (
        <div
          ref={ref}
          className={classNames(styles.container, styles.compact, {
            [styles.disabled]: disabled,
          })}
          data-size={size}
          data-testid={testId}
          role="group"
          aria-label={accessibleLabel}
        >
          {/* Left label - always strikethrough (represents "off" state) */}
          <span
            className={classNames(
              styles.text,
              styles.clickable,
              styles.compactLabel,
              styles.strikethrough,
              {
                [styles.active]: !checked,
              },
            )}
            onClick={handleSetOff}
            onKeyDown={(e) => handleTextKeyDown(e, handleSetOff)}
            tabIndex={disabled ? -1 : 0}
            role="button"
            aria-pressed={!checked}
            aria-label={`${resolvedCompactLabel} disabled`}
          >
            {resolvedCompactLabel}
          </span>

          <div
            className={classNames(styles.toggle, {
              [styles.on]: checked,
            })}
            onClick={handleToggle}
            onKeyDown={handleKeyDown}
            role="switch"
            aria-checked={checked}
            aria-disabled={disabled}
            aria-label={`${accessibleLabel}, currently ${currentStateLabel}`}
            tabIndex={disabled ? -1 : 0}
            id={toggleId}
          >
            <div className={styles.whitespace} />
            <div className={styles.toggleCircle} aria-hidden="true" />
          </div>

          {/* Right label - never strikethrough (represents "on" state) */}
          <span
            className={classNames(
              styles.text,
              styles.clickable,
              styles.compactLabel,
              {
                [styles.active]: checked,
              },
            )}
            onClick={handleSetOn}
            onKeyDown={(e) => handleTextKeyDown(e, handleSetOn)}
            tabIndex={disabled ? -1 : 0}
            role="button"
            aria-pressed={checked}
            aria-label={`${resolvedCompactLabel} enabled`}
          >
            {resolvedCompactLabel}
          </span>
        </div>
      );
    }

    // Responsive compact mode: render both variants, CSS controls visibility
    if (responsiveCompact) {
      return (
        <div
          ref={ref}
          className={classNames(styles.responsiveWrapper, {
            [styles.disabled]: disabled,
          })}
          data-size={size}
          data-testid={testId}
          role="group"
          aria-label={accessibleLabel}
        >
          {/* Standard variant - hidden on narrow screens */}
          <div className={styles.standardVariant} data-size={size}>
            <span
              className={classNames(styles.text, styles.clickable, {
                [styles.active]: !checked,
              })}
              onClick={handleSetOff}
              onKeyDown={(e) => handleTextKeyDown(e, handleSetOff)}
              tabIndex={disabled ? -1 : 0}
              role="button"
              aria-pressed={!checked}
              aria-label={`Set to ${textOff}`}
            >
              {textOff}
            </span>

            <div
              className={classNames(styles.toggle, {
                [styles.on]: checked,
              })}
              onClick={handleToggle}
              onKeyDown={handleKeyDown}
              role="switch"
              aria-checked={checked}
              aria-disabled={disabled}
              aria-label={`${accessibleLabel}, currently ${currentStateLabel}`}
              tabIndex={disabled ? -1 : 0}
              id={toggleId}
            >
              <div className={styles.whitespace} />
              <div className={styles.toggleCircle} aria-hidden="true" />
            </div>

            <span
              className={classNames(styles.text, styles.clickable, {
                [styles.active]: checked,
              })}
              onClick={handleSetOn}
              onKeyDown={(e) => handleTextKeyDown(e, handleSetOn)}
              tabIndex={disabled ? -1 : 0}
              role="button"
              aria-pressed={checked}
              aria-label={`Set to ${textOn}`}
            >
              {textOn}
            </span>
          </div>

          {/* Compact variant - shown on narrow screens */}
          <div
            className={classNames(styles.compactVariant, styles.compact)}
            data-size={size}
          >
            <span
              className={classNames(
                styles.text,
                styles.clickable,
                styles.compactLabel,
                styles.strikethrough,
                {
                  [styles.active]: !checked,
                },
              )}
              onClick={handleSetOff}
              onKeyDown={(e) => handleTextKeyDown(e, handleSetOff)}
              tabIndex={disabled ? -1 : 0}
              role="button"
              aria-pressed={!checked}
              aria-label={`${resolvedCompactLabel} disabled`}
            >
              {resolvedCompactLabel}
            </span>

            <div
              className={classNames(styles.toggle, {
                [styles.on]: checked,
              })}
              onClick={handleToggle}
              onKeyDown={handleKeyDown}
              role="switch"
              aria-checked={checked}
              aria-disabled={disabled}
              aria-label={`${accessibleLabel}, currently ${currentStateLabel}`}
              tabIndex={-1}
              id={`${toggleId}-compact`}
            >
              <div className={styles.whitespace} />
              <div className={styles.toggleCircle} aria-hidden="true" />
            </div>

            <span
              className={classNames(
                styles.text,
                styles.clickable,
                styles.compactLabel,
                {
                  [styles.active]: checked,
                },
              )}
              onClick={handleSetOn}
              onKeyDown={(e) => handleTextKeyDown(e, handleSetOn)}
              tabIndex={disabled ? -1 : 0}
              role="button"
              aria-pressed={checked}
              aria-label={`${resolvedCompactLabel} enabled`}
            >
              {resolvedCompactLabel}
            </span>
          </div>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={classNames(styles.container, {
          [styles.disabled]: disabled,
        })}
        data-size={size}
        data-testid={testId}
        role="group"
        aria-label={accessibleLabel}
      >
        <span
          className={classNames(styles.text, styles.clickable, {
            [styles.active]: !checked,
          })}
          onClick={handleSetOff}
          onKeyDown={(e) => handleTextKeyDown(e, handleSetOff)}
          tabIndex={disabled ? -1 : 0}
          role="button"
          aria-pressed={!checked}
          aria-label={`Set to ${textOff}`}
        >
          {textOff}
        </span>

        <div
          className={classNames(styles.toggle, {
            [styles.on]: checked,
          })}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          role="switch"
          aria-checked={checked}
          aria-disabled={disabled}
          aria-label={`${accessibleLabel}, currently ${currentStateLabel}`}
          tabIndex={disabled ? -1 : 0}
          id={toggleId}
        >
          <div className={styles.whitespace} />
          <div className={styles.toggleCircle} aria-hidden="true" />
        </div>

        <span
          className={classNames(styles.text, styles.clickable, {
            [styles.active]: checked,
          })}
          onClick={handleSetOn}
          onKeyDown={(e) => handleTextKeyDown(e, handleSetOn)}
          tabIndex={disabled ? -1 : 0}
          role="button"
          aria-pressed={checked}
          aria-label={`Set to ${textOn}`}
        >
          {textOn}
        </span>
      </div>
    );
  },
);

Toggle.displayName = "Toggle";

export default Toggle;
